/**
 * Štítek statistické významnosti.
 *
 * Hladiny MUSÍ odpovídat significance_label() z backend core/stats.py — testuje
 * to SignificanceBadge.test.tsx. Když srovnání nese `significance_cs` (backend
 * si text generuje sám), zobrazí se přednostně ten: tabulka a LLM report pak
 * nikdy netvrdí každý něco jiného. Lokální mapování řídí barvu a je zálohou.
 *
 * Barva sama význam nenese (je to status škála), proto vždy s textovým popiskem.
 */
import {
  normalizeComparison,
  significanceOf,
  type AnyComparison,
  type SignificanceLevel,
} from '@/lib/significance';
import { formatPValue, formatPb, formatN } from '@/lib/formatCz';

export interface SignificanceBadgeProps {
  /** buď hotové srovnání z API… */
  comparison?: AnyComparison | null;
  /** …nebo samotná p-hodnota */
  pValue?: number | null;
  /** zobrazit i rozdíl v pb */
  showDiff?: boolean;
  /** zobrazit n (pravidlo: u srovnání je n vždy vidět) */
  showN?: boolean;
  compact?: boolean;
}

const LEVEL_CLASS: Record<SignificanceLevel, string> = {
  'highly-significant': 'sig-badge--strong',
  'significant-01': 'sig-badge--strong',
  'significant-05': 'sig-badge--significant',
  borderline: 'sig-badge--borderline',
  'not-significant': 'sig-badge--none',
};

export function SignificanceBadge({
  comparison,
  pValue,
  showDiff = false,
  showN = false,
  compact = false,
}: SignificanceBadgeProps) {
  const c = normalizeComparison(comparison);
  const p = c ? c.pValue : pValue;
  const info = significanceOf(p);

  if (p === null || p === undefined) {
    return (
      <span className="sig-badge sig-badge--none" title="Srovnání není k dispozici">
        —
      </span>
    );
  }

  const fullLabel = c?.significanceText || info.label;
  const diffText = c ? c.diffText || formatPb(c.diff) : null;

  const title = [
    fullLabel,
    `p = ${formatPValue(p)}`,
    c ? formatN(c.nTotal) : null,
    diffText ? `rozdíl ${diffText}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <span className={`sig-badge ${LEVEL_CLASS[info.level]}`} title={title}>
      <span className="sig-badge__dot" aria-hidden="true" />
      <span className="sig-badge__label">{compact ? info.short : fullLabel}</span>
      {showDiff && diffText ? (
        <span className="sig-badge__diff">{diffText}</span>
      ) : null}
      {showN && c ? <span className="sig-badge__n">{formatN(c.nTotal)}</span> : null}
    </span>
  );
}
